import React from "react";
import isNode from "is-node";
import Helmet from "react-helmet";

import { formatImage } from "src/helpers/formatImage";

import { Props } from "./types";

const getSrcSet = (src: string, ratio: number) => {
  const array = [];
  array.push(`${formatImage(src, 600, ratio)} 600w`);
  array.push(`${formatImage(src, 1000, ratio)} 1000w`);
  array.push(`${formatImage(src, 1300, ratio)} 1300w`);
  array.push(`${formatImage(src, 1600, ratio)} 1600w`);
  array.push(`${formatImage(src, 2000, ratio)} 2000w`);
  array.push(`${formatImage(src, 2400, ratio)} 2400w`);
  return array.join(", ");
};

class Image extends React.Component<Props> {
  imageRef: HTMLImageElement | null = null;
  observer: IntersectionObserver | null = null;

  constructor(props: Props) {
    super(props);
    if (!isNode && typeof IntersectionObserver !== "undefined") {
      this.observer = new IntersectionObserver(this.observeHandler, {
        rootMargin: "0px 0px 500px 0px",
      });
    }
  }

  loadImage = (img: HTMLImageElement) => {
    const { onImageLoaded } = this.props;
    const src = img.getAttribute("data-src") || "";
    const srcSet = img.getAttribute("data-srcset") || "";

    const reveal = () => {
      img.style.opacity = "1";
      if (onImageLoaded) {
        onImageLoaded();
      }
    };

    img.onload = reveal;
    img.onerror = reveal;

    if (srcSet) {
      img.setAttribute("srcset", srcSet);
    }
    img.setAttribute("src", src);

    if (img.complete) {
      reveal();
    }
  };

  observeHandler = (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.isIntersecting && this.imageRef) {
        this.loadImage(this.imageRef);
        if (this.observer) {
          this.observer.unobserve(entry.target);
        }
      }
    }
  };

  get placeholder() {
    const { ratio } = this.props;
    if (!ratio) {
      // Return an empty pixel
      return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP89fkzEwAIswLkYLzKuwAAAABJRU5ErkJggg==";
    }
    const viewBox = `0 0 ${ratio} 1`;
    return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}"><rect fill="${encodeURIComponent(
      "#FFFFFF00"
    )}" width="100%" height="100%" /></svg>`;
  }

  render() {
    let { src, alt, size = "50vw", ratio, className, isCritical } = this.props;
    if (src && src.endsWith(".svg")) {
    } else {
      if (className) {
        className = className + " o-image";
      } else {
        className = "o-image";
      }
      if (isCritical) {
        className = className + " critical";
      }
    }

    const srcSet = getSrcSet(src, ratio);
    const formatSrc = formatImage(src, 2400, ratio);

    if (typeof alt !== "string") {
      alt = "";
    }

    if (src && src.endsWith(".svg")) {
      return <img src={src} alt={alt} className={className} />;
    }

    return (
      <React.Fragment>
        {isCritical && (
          <Helmet defer={false}>
            <link
              rel="preload"
              as="image"
              href={formatSrc}
              imageSrcSet={srcSet}
              imageSizes={`(max-width: 600px) 100vw,${size}`}
            />
          </Helmet>
        )}
        <img
          ref={(x) => {
            if (x && !isCritical) {
              this.imageRef = x;
              if (this.observer) {
                this.observer.observe(x);
              } else if (!isNode) {
                this.loadImage(x);
              }
            }
          }}
          className={className}
          sizes={`(max-width: 600px) 100vw,${size}`}
          srcSet={isCritical ? srcSet : undefined}
          src={isCritical ? formatSrc : this.placeholder}
          data-srcset={isCritical ? undefined : srcSet}
          data-src={isCritical ? undefined : formatSrc}
          alt={alt}
          loading={isCritical ? "eager" : undefined}
        />
      </React.Fragment>
    );
  }
}

export default Image;
